// networkHandlers.ts - Multiplayer event handlers extracted from main.js

import { gameState, getFactions } from './game.ts';
import { BugReport } from './bug-report.ts';
import { StandardRemoteDriver } from './drivers/standard.ts';
import { getSession, touchSession } from './reconnection.ts';
import * as PlacementPhase from './placementPhase.ts';
import type { StoredSessionExtras } from './reconnection.ts';
import type {
    AbilityResult,
    ErrorMessage,
    FireError,
    GameStart,
    MineSunkShip,
    OpponentAbility,
    OpponentJoined,
    OpponentShot,
    PassiveEventResult,
    PassiveTriggered,
    QuickplayMatched,
    QuickplayQueueUpdate,
    RoomCreated,
    RoomGone,
    RoomJoined,
    ShotResult,
    TournamentMatchComplete,
    TournamentResultConfirmed,
    TurnChange,
    TurnStartMechanicResult
} from '../../shared/protocol.ts';
import type { Coord } from '../../shared/abilityLogic.ts';
import type { NetworkManager } from './network.ts';
import type { TacticalInitOptions } from './tactical/main.ts';
import type { AbilityManager } from './abilities.ts';
import type { Ship } from './game.ts';
import type { RoguelikeRunStateView } from './battlePhase.ts';
import type { renderer as rendererInstance } from './renderer.ts';
import { createAbilityRenderEnv, renderOpponentAbility, renderShotPassives } from './abilityRenderer.ts';
import type { abilityRenderer as abilityRendererInstance } from './abilityRenderer.ts';
// tactical/main.js is loaded lazily via ctx.loadTacticalModule() to avoid
// a static import that would defeat code-splitting.

type Renderer = typeof rendererInstance;
type AbilityRenderer = typeof abilityRendererInstance;
type RoguelikeModule = typeof import('./roguelike.ts');

/** Lazily-loaded tactical module surface used by the MP launch paths. */
interface TacticalModule {
    initTactical(options: TacticalInitOptions): Promise<unknown>;
    cleanupTactical(): void;
}

/** The slice of main.js's shared context that the network handlers read. */
export interface NetworkHandlersContext {
    networkManager: NetworkManager;
    renderer: Renderer;
    abilityManager: AbilityManager;
    abilityRenderer: AbilityRenderer;
    saveSession: (roomCode: string, sessionToken: string, faction: string | null, extra?: StoredSessionExtras) => void;
    clearSession: () => void;
    getDriver: () => StandardRemoteDriver | null;
    setDriver: (driver: StandardRemoteDriver) => void;
    loadTacticalModule: () => Promise<TacticalModule>;
    initDraftScreen?: (mode: string) => void;
    startPickingPhase: (pool: string[]) => void;
    initializePlacement: () => void;
    startBattle: () => void;
    endGame: (won: boolean) => void;
    playSound: (name: string) => void;
    updateBlitzCounter: () => void;
    clearFireProcessing: () => void;
    incrementTurnCount: () => void;
    stopRematchCountdown: () => void;
    handleAbilityResult: (result: AbilityResult) => void;
    setQuickplayMode: (value: boolean) => void;
    getRoguelike: () => RoguelikeModule;
    getRoguelikeRunState: () => RoguelikeRunStateView | null;
}

let ctx: NetworkHandlersContext;

/**
 * Server events that mean the game is live. Each one slides the stored
 * reconnection session's expiry forward (touchSession) so a refresh late in a
 * long game can still rejoin — rooms now expire on inactivity, not age.
 */
const SESSION_ACTIVITY_EVENTS = [
    'opponent-joined', 'opponent-ready', 'game-start', 'shot-result', 'opponent-shot',
    'turn-change', 'ability-result', 'opponent-ability', 'passive-triggered',
    'rematch-start', 'rejoin-success', 'reconnected',
    'tactical-fleet-accepted', 'tactical-placement-start', 'tactical-placements-accepted',
    'tactical-battle-start', 'tactical-move-result', 'tactical-phase-change',
    'tactical-fire-result', 'tactical-ability-result', 'tactical-opponent-turn',
    'tactical-rejoin-success', 'tactical-rematch-start'
] as const;

/**
 * Clear the mode flags other modules set on gameState outside reset()
 * (roguelikeUI, strategy, tournament, reconnection), so a forced return to
 * the menu can't leak e.g. isTacticalRoguelike into the next ordinary game.
 */
export function clearExternalModeFlags(state: typeof gameState = gameState): void {
    state.isStrategy = false;
    state.isTacticalRoguelike = false;
    state.isTournament = false;
    state.tournamentCode = null;
    state.tournamentGameMode = null;
    state.tournamentFactionDraft = undefined;
    state.tournamentMatchResult = undefined;
    state.relentlessTriggeredThisTurn = false;
    state.probes = undefined;
    state.mines = undefined;
    state.pendingOpponentSunkShips = null;
}

export function setup(context: NetworkHandlersContext): void {
    ctx = context;
}

// Helper function to update queue display
function updateQueueDisplay(position: number, estimatedWait: number | null): void {
    const posEl = document.getElementById('queue-pos');
    const timeEl = document.getElementById('queue-time');

    if (posEl) posEl.textContent = `#${position}`;
    if (timeEl) {
        if (estimatedWait === null) {
            timeEl.textContent = 'Calculating...';
        } else {
            const seconds = Math.round(estimatedWait / 1000);
            timeEl.textContent = `~${seconds} sec`;
        }
    }
}

/** Turn-start mechanic results carry `type`; passive triggers never do. */
function isMechanicResult(result: PassiveEventResult): result is TurnStartMechanicResult {
    return typeof (result as TurnStartMechanicResult).type === 'string';
}

// Network event handlers
export function setupNetworkHandlers(): void {
    const { networkManager, renderer, abilityManager, abilityRenderer } = ctx;

    for (const event of SESSION_ACTIVITY_EVENTS) {
        networkManager.on(event, () => touchSession());
    }

    networkManager.on('room-created', async (rawData?: unknown) => {
        const data = rawData as RoomCreated;
        // Server-hosted single-player (AI room): the SP launch path owns the
        // whole flow (it created the room and listens for this event itself).
        // Don't save a session or re-init tactical from here.
        if (data.aiRoom) return;

        gameState.roomCode = data.roomCode;
        gameState.playerId = data.playerId ?? null;
        if (data.abilities) {
            abilityManager.updateFromServer(data.abilities);
        }
        // Save session for reconnection
        if (data.sessionToken) {
            ctx.saveSession(data.roomCode, data.sessionToken, gameState.faction);
        }

        // Store tournament settings from server response
        gameState.isTournament = !!data.isTournamentMatch;
        if (data.tournamentGameMode) {
            gameState.tournamentGameMode = data.tournamentGameMode;
        }
        if (data.tournamentFactionDraft !== undefined) {
            gameState.tournamentFactionDraft = data.tournamentFactionDraft;
        }

        // Tournament faction draft: stay on draft screen, waiting for opponent
        if (data.tournamentFactionDraft) {
            // Draft screen is already shown; update status
            const statusEl = document.getElementById('draft-status');
            if (statusEl) statusEl.textContent = 'Waiting for opponent to join...';
            return;
        }

        // Create remote driver for MP battle actions
        const driver = ctx.getDriver();
        if (driver) driver.dispose();
        const newDriver = new StandardRemoteDriver(networkManager, data.roomCode);
        ctx.setDriver(newDriver);

        // Tactical MP: launch tactical mode and show fleet builder while waiting
        if (data.gameType === 'tactical') {
            const tacticalMod = await ctx.loadTacticalModule();
            await tacticalMod.initTactical({
                faction: gameState.faction,
                mode: 'multiplayer',
                socket: networkManager,
                mainRenderer: renderer,
                roomCode: data.roomCode
            });
            // Show room code overlay so the creator can share it
            const codeDisplay = document.getElementById('display-room-code');
            if (codeDisplay) codeDisplay.textContent = data.roomCode;
            return;
        }

        document.getElementById('display-room-code')!.textContent = data.roomCode;
        renderer.showScreen('waiting-screen');
    });

    networkManager.on('room-joined', async (rawData?: unknown) => {
        const data = rawData as RoomJoined;
        gameState.roomCode = data.roomCode;
        gameState.playerId = data.playerId ?? null;
        gameState.opponentName = data.opponentName!;
        gameState.opponentFaction = data.opponentFaction!;
        if (data.abilities) {
            abilityManager.updateFromServer(data.abilities);
        }
        // Save session for reconnection
        if (data.sessionToken) {
            ctx.saveSession(data.roomCode, data.sessionToken, gameState.faction);
        }

        // Store tournament settings from server response
        gameState.isTournament = !!data.isTournamentMatch;
        if (data.tournamentGameMode) {
            gameState.tournamentGameMode = data.tournamentGameMode;
        }
        if (data.tournamentFactionDraft !== undefined) {
            gameState.tournamentFactionDraft = data.tournamentFactionDraft;
        }

        // Tournament faction draft: handle draft in progress (reconnection) or fresh start
        if (data.tournamentFactionDraft) {
            renderer.showScreen('draft-screen');

            // If server sent draft state (reconnection during active draft), restore it
            if (data.draftPhase && data.draftPhase !== 'complete' && ctx.initDraftScreen) {
                if (data.draftPhase === 'picking' && data.draftPool) {
                    ctx.initDraftScreen('waiting');
                    ctx.startPickingPhase(data.draftPool);
                    if (data.draftAlreadyPicked) {
                        const statusEl2 = document.getElementById('draft-status');
                        const actionsEl = document.getElementById('draft-actions');
                        if (statusEl2) statusEl2.textContent = 'Pick submitted! Waiting for opponent...';
                        if (actionsEl) actionsEl.style.display = 'none';
                        document.querySelectorAll('.draft-faction-card').forEach(c => c.classList.add('disabled'));
                    }
                } else if (data.draftPhase === 'nominating') {
                    ctx.initDraftScreen('nominating');
                    if (data.draftAlreadyNominated) {
                        const statusEl2 = document.getElementById('draft-status');
                        const actionsEl = document.getElementById('draft-actions');
                        if (statusEl2) statusEl2.textContent = 'Nominations submitted! Waiting for opponent...';
                        if (actionsEl) actionsEl.style.display = 'none';
                        document.querySelectorAll('.draft-faction-card').forEach(c => c.classList.add('disabled'));
                    }
                } else {
                    ctx.initDraftScreen('waiting');
                }
            } else {
                // Fresh join — draft-start event will arrive shortly
                const statusEl = document.getElementById('draft-status');
                if (statusEl) statusEl.textContent = 'Draft starting...';
            }
            return;
        }

        // Create remote driver for MP battle actions
        const driver = ctx.getDriver();
        if (driver) driver.dispose();
        const newDriver = new StandardRemoteDriver(networkManager, data.roomCode);
        ctx.setDriver(newDriver);

        // Tactical MP: launch tactical mode for the joiner
        if (data.gameType === 'tactical') {
            // Tournament seat reclaim: the server's seat faction wins over the
            // local (possibly draft-placeholder) selection
            if (data.faction) gameState.faction = data.faction;
            const tacticalMod = await ctx.loadTacticalModule();
            await tacticalMod.initTactical({
                faction: gameState.faction,
                mode: 'multiplayer',
                socket: networkManager,
                mainRenderer: renderer,
                roomCode: data.roomCode
            });
            // Reclaimed past fleet building: resume the live game (the
            // tactical driver's onRejoin routes to placement/battle)
            if (data.tacticalPhase && data.tacticalPhase !== 'fleet-build') {
                networkManager.tacticalRejoin(data.roomCode, data.sessionToken);
            }
            return;
        }

        renderer.showScreen('placement-screen');
        BugReport.setCurrentScreen('placement');
        ctx.initializePlacement();
    });

    networkManager.on('join-error', (rawData?: unknown) => {
        const data = rawData as ErrorMessage;
        renderer.showToast(data.message, true);
    });

    networkManager.on('opponent-joined', (rawData?: unknown) => {
        const data = rawData as OpponentJoined;
        gameState.opponentName = data.opponentName;
        gameState.opponentFaction = data.opponentFaction;

        // Set enemy faction colors for ship graphics
        const factions = getFactions();
        const enemyFactionData = factions[data.opponentFaction];
        if (enemyFactionData?.colors) {
            renderer.setFactionColors(null, enemyFactionData.colors);
        }

        // Update draft flag from event if present (ensures P1 has it even if cached state was lost)
        if (data.tournamentFactionDraft !== undefined) {
            gameState.tournamentFactionDraft = data.tournamentFactionDraft;
        }

        // Tournament faction draft: don't go to placement yet; draft-start event handles it
        if (gameState.tournamentFactionDraft) {
            return;
        }

        // Tactical MP: opponent joined -- the room creator already has tactical
        // initialized from room-created, so just log the opponent info
        if (data.gameType === 'tactical') {
            return;
        }

        renderer.showScreen('placement-screen');
        BugReport.setCurrentScreen('placement');
        ctx.initializePlacement();
    });

    networkManager.on('opponent-ready', () => {
        renderer.updateOpponentStatus(gameState.opponentName, true, true);
    });

    // Opponent took their READY back — they're editing their layout again
    networkManager.on('opponent-unready', () => {
        renderer.updateOpponentStatus(gameState.opponentName, false, true);
        renderer.showToast(`${gameState.opponentName || 'Opponent'} is repositioning their fleet`);
    });

    // Server cleared our ready flag: placement is editable again
    networkManager.on('ships-unready-ok', () => {
        PlacementPhase.setPlacementLocked(false);
        PlacementPhase.rollbackRoguelikeCommit();
    });

    // Un-ready refused (usually: the opponent readied first and the battle
    // already started — game-start follows). Stay locked on the submitted
    // layout; just re-arm the button in case the game hasn't started.
    networkManager.on('ships-unready-error', (rawData?: unknown) => {
        const data = rawData as ErrorMessage | undefined;
        renderer.showToast(data?.message || 'Could not unready', true);
        const btn = document.getElementById('btn-unready') as HTMLButtonElement | null;
        if (btn) {
            btn.disabled = false;
            btn.textContent = 'UNREADY';
        }
    });

    // Server rejected ships-placed.
    // "Not a room participant": socket.io silently swapped the underlying
    // socket between room-join and clicking Ready: the new socket id was never
    // registered with the room, so the server can't tie ships-placed back to a
    // player. Auto-rejoin (reconnection.ts socket-reconnected handler) normally
    // repairs this, but very short blips can race the click. Resync via
    // rejoin-room so the player can retry instead of being silently stuck on
    // "WAITING...".
    // Any other rejection (invalid fleet, bad payload) can't be fixed by a
    // resync: surface it and hand placement back to the player.
    networkManager.on('ships-placed-error', (rawData?: unknown) => {
        const data = rawData as ErrorMessage | undefined;
        const session = getSession();
        // Resync via rejoin-room for MP and for server-hosted SP (AI room)
        // sessions alike — both ride the same rejoin pipeline.
        const canResync = data?.message === 'Not a room participant'
            && session && !gameState.isTactical
            && (gameState.mode === 'multiplayer' || session.aiRoom);
        if (canResync) {
            renderer.showToast('Connection hiccup — resyncing...', true);
            networkManager.rejoinRoom(session.roomCode, session.sessionToken);
        } else {
            const reason = data?.message ? ` (${data.message})` : '';
            renderer.showToast(`Could not submit ships${reason}. Please try again.`, true);
            PlacementPhase.setPlacementLocked(false);
            PlacementPhase.rollbackRoguelikeCommit();
        }
    });

    networkManager.on('game-start', (rawData?: unknown) => {
        const data = rawData as GameStart;
        gameState.isMyTurn = data.currentTurn === gameState.playerId;
        // Battle shows exactly the layout the server accepted
        PlacementPhase.applySubmittedLayout();
        ctx.startBattle();

        // Server-hosted SP roguelike battles: the server applied the
        // pre-battle damage (shipDamage carry-over + pendingDamage random
        // hits) to the authoritative board and reports the cells here —
        // mirror them onto the local board/ships.
        if (gameState.mode === 'singleplayer' && gameState.isRoguelike) {
            const applied = data.roguelike?.appliedDamage || [];
            for (const dmg of applied) {
                gameState.playerBoard.shots.set(`${dmg.x},${dmg.y}`, { hit: true, shipId: dmg.shipId });
                const ship = gameState.ships.find(s => s.id === dmg.shipId);
                if (ship) ship.hit(dmg.x, dmg.y);
            }
            if (applied.length > 0) {
                renderer.renderYourBoard(gameState.playerBoard);
                renderer.renderShipSilhouettes(gameState.ships, renderer.yourBoard);
                renderer.renderShipsStatus(gameState.ships, 'your-ships-status');
            }
            // Pending event damage was resolved server-side (it's part of
            // appliedDamage) — clear it from the run like the local path does
            const runState = ctx.getRoguelikeRunState();
            if (runState && runState.pendingDamage > 0) {
                runState.pendingDamage = 0;
                ctx.getRoguelike().saveActiveRun(runState);
            }
        }

        // Set opponent ability state for tracking display
        if (data.opponentAbilityState) {
            abilityManager.setOpponentState(data.opponentAbilityState);
        }

        // Handle turn start passives
        if (data.turnStartPassives && data.turnStartPassives.results) {
            for (const result of data.turnStartPassives.results) {
                abilityRenderer.showPassiveTriggered(result);
            }
        }
    });

    networkManager.on('shot-result', (rawData?: unknown) => {
        const data = rawData as ShotResult;
        // Handle deflected shots - don't record, cell stays selectable
        if (data.deflected) {
            ctx.playSound('splash');
            renderer.showToast('Shot was deflected by shield!', true);
            const enemyBoard = document.getElementById('enemy-board');
            if (enemyBoard) {
                abilityRenderer.showShieldDeflection(data.x, data.y, enemyBoard);
            }
        } else if (data.armorAbsorbed) {
            // Roguelike flagship armor absorbed the killing blow: the server
            // un-hits the cell and doesn't record the shot, so the cell must
            // stay targetable here too (mirrors the deflect handling above —
            // recording it would make handleFire block the finishing shot).
            ctx.playSound('splash');
            renderer.showToast("The flagship's armor absorbed the hit!", true);
            const enemyBoard = document.getElementById('enemy-board');
            if (enemyBoard) {
                abilityRenderer.showShieldDeflection(data.x, data.y, enemyBoard);
            }
        } else if (data.isDecoy) {
            // Decoy fully destroyed - reveal all cells as decoy
            gameState.recordPlayerShot(false);
            const revealedCells = data.revealedCells || [{ x: data.x, y: data.y }];
            const enemyBoard = document.getElementById('enemy-board');

            // Convert any previous "hit" markers to decoy markers
            for (const c of revealedCells) {
                const wasHit = gameState.enemyShots.get(`${c.x},${c.y}`)?.hit;
                gameState.enemyShots.set(`${c.x},${c.y}`, { hit: false, isDecoy: true });

                // If this cell was previously shown as a hit, clear that marker
                if (wasHit && enemyBoard) {
                    const cell = enemyBoard.querySelector(`[data-x="${c.x}"][data-y="${c.y}"]`);
                    if (cell) {
                        cell.classList.remove('hit');
                    }
                }
            }

            ctx.playSound('splash');
            const msg = (data.decoySize ?? 0) > 1 ? `${data.decoySize}-cell decoy destroyed!` : 'You hit a decoy!';
            renderer.showToast(msg);

            if (enemyBoard) {
                abilityRenderer.showDecoyReveal(revealedCells, enemyBoard);
            }
        } else if (data.hit) {
            // Record hit
            gameState.recordPlayerShot(true);
            gameState.enemyShots.set(`${data.x},${data.y}`, { hit: true });
            ctx.playSound('explosion');
            renderer.markHit(null, data.x, data.y, true);

            if (data.sunk) {
                ctx.playSound('destroyed');
                renderer.showToast(`Enemy ${data.shipClass} destroyed!`);

                // Trigger cascade explosion animation if ship cells provided
                if (data.sunkShipCells && data.sunkShipCells.length > 0) {
                    // Mark all cells as hit first
                    for (const cell of data.sunkShipCells) {
                        gameState.enemyShots.set(`${cell.x},${cell.y}`, { hit: true });
                    }
                    // Create a ship-like object for the animation
                    // (captured const so the closure keeps the narrowed Coord[])
                    const sunkShipCells = data.sunkShipCells;
                    const sunkShip = {
                        getCells: () => sunkShipCells
                    };
                    renderer.markSunk(sunkShip, true, { x: data.x, y: data.y });
                }

                // Update opponent ship status tracking
                if (gameState.opponentShipStatus) {
                    const ship = gameState.opponentShipStatus.find(s => s.class === data.shipClass && !s.sunk);
                    if (ship) {
                        ship.sunk = true;
                        renderer.renderShipsStatus(
                            gameState.opponentShipStatus.map(s => ({ class: s.class, isSunk: () => s.sunk })),
                            'enemy-ships-status'
                        );
                    }
                }
            }
        } else {
            // Record miss
            gameState.recordPlayerShot(false);
            gameState.enemyShots.set(`${data.x},${data.y}`, { hit: false });
            ctx.playSound('splash');
            renderer.markMiss(null, data.x, data.y, true);
        }

        // Handle splash damage results
        if (data.splashResults && data.splashResults.applied) {
            renderer.showToast('Splash Damage: Adjacent cells hit!');
            for (const splash of data.splashResults.splashHits || []) {
                if (splash.alreadyHit) continue; // Skip cells that were already hit
                if (splash.armorAbsorbed) continue; // Flagship armor: cell stays targetable
                gameState.enemyShots.set(`${splash.x},${splash.y}`, { hit: splash.hit });
                if (splash.hit) {
                    renderer.markHit(null, splash.x, splash.y, true);
                    // Handle ship sunk by splash damage
                    if (splash.sunk && splash.sunkShipCells) {
                        ctx.playSound('destroyed');
                        renderer.showToast(`Enemy ${splash.shipClass} destroyed!`);
                        for (const cell of splash.sunkShipCells) {
                            gameState.enemyShots.set(`${cell.x},${cell.y}`, { hit: true });
                        }
                        const sunkShipCells = splash.sunkShipCells;
                        const sunkShip = { getCells: () => sunkShipCells };
                        renderer.markSunk(sunkShip, true, { x: splash.x, y: splash.y });
                    }
                } else {
                    renderer.markMiss(null, splash.x, splash.y, true);
                }
            }
        }

        // Show passive ability triggers (our shot landed → our passives)
        renderShotPassives(
            data.passiveResults, 'own-action',
            createAbilityRenderEnv({ gameState, renderer, abilityManager }),
            { x: data.x, y: data.y }
        );

        // Update ability state
        if (data.abilityState) {
            abilityManager.updateFromServer(data.abilityState);
        }

        renderer.renderEnemyBoard(gameState.enemyShots);
        abilityRenderer.renderAbilities(data.extraShotRemaining || false);
        abilityRenderer.updateBuffs();

        // Blitz over the wire: the server never gives the AI a turn — each
        // shot-result is followed by a turn-change back to the human. Keep
        // the shots-fired counter in sync.
        if (gameState.isBlitz) {
            ctx.updateBlitzCounter();
        }

        // Clear fire processing lock after shot result received
        ctx.clearFireProcessing();

        if (data.gameOver) {
            ctx.endGame(data.winner === gameState.playerId);
        }
    });

    networkManager.on('opponent-shot', (rawData?: unknown) => {
        const data = rawData as OpponentShot;
        // Update opponent's ability state if provided
        if (data.opponentAbilityState) {
            abilityManager.setOpponentState(data.opponentAbilityState);
        }

        // Handle deflected shots (our shield blocked the hit) - don't record
        if (data.deflected) {
            ctx.playSound('splash');
            renderer.showToast('Your shield deflected the shot!');
            const yourBoard = document.getElementById('your-board');
            if (yourBoard) {
                abilityRenderer.showShieldDeflection(data.x, data.y, yourBoard);
            }
        } else if (data.isDecoy) {
            // Opponent fully destroyed our decoy - reveal and clear all cells
            ctx.playSound('splash');
            const msg = (data.decoySize ?? 0) > 1 ? `Opponent destroyed your ${data.decoySize}-cell decoy!` : 'Opponent hit your decoy!';
            renderer.showToast(msg);
            // Use server-provided cells or fall back to local tracking
            const revealedCells = data.revealedCells || abilityManager.revealDecoy(data.x, data.y);
            // Also mark local decoy as revealed
            abilityManager.revealDecoy(data.x, data.y);
            const yourBoard = document.getElementById('your-board');
            if (yourBoard) {
                abilityRenderer.showDecoyReveal(revealedCells, yourBoard);
                // Clear all cells completely after animation
                setTimeout(() => {
                    for (const c of revealedCells) {
                        const cell = yourBoard.querySelector(`[data-x="${c.x}"][data-y="${c.y}"]`);
                        if (cell) {
                            cell.classList.remove('decoy-placed', 'decoy-revealed', 'miss', 'hit');
                        }
                    }
                }, 1500);
            }
        } else if (data.hit) {
            // Check if this is actually hitting one of our decoy cells (multi-cell decoy partial hit)
            const hitDecoy = abilityManager.decoyPositions.find(d => {
                if (d.revealed) return false;
                if (d.x === data.x && d.y === data.y) return true;
                if (d.cells) {
                    return d.cells.some(c => c.x === data.x && c.y === data.y);
                }
                return false;
            });

            if (hitDecoy) {
                // Opponent hit part of our multi-cell decoy - show as hit but we know it's a decoy
                ctx.playSound('explosion'); // Sound like a real hit to maintain the deception
                renderer.showToast('Opponent hit your decoy cell');
                const yourBoard = document.getElementById('your-board');
                if (yourBoard) {
                    const cell = yourBoard.querySelector(`[data-x="${data.x}"][data-y="${data.y}"]`);
                    if (cell) {
                        // Mark as hit but keep decoy styling underneath
                        cell.classList.add('hit');
                    }
                }
            } else {
                // Record hit on our board (real ship hit)
                gameState.playerBoard.shots.set(`${data.x},${data.y}`, { hit: true });
                ctx.playSound('explosion');
                renderer.markHit(gameState.playerBoard, data.x, data.y, false);

                // Find and update the hit ship
                const shipId = gameState.playerBoard.grid[data.y][data.x];
                let hitShip: Ship | null | undefined = null;
                if (shipId) {
                    hitShip = gameState.ships.find(s => s.id === shipId);
                    if (hitShip) {
                        hitShip.hit(data.x, data.y);
                    }
                }

                if (data.sunk && hitShip) {
                    ctx.playSound('destroyed');
                    renderer.showToast(`Your ${data.shipClass} was destroyed!`, true);
                    gameState.recordShipLost();
                    renderer.markSunk(hitShip, false, { x: data.x, y: data.y });
                } else if (data.sunk) {
                    ctx.playSound('destroyed');
                    renderer.showToast(`Your ${data.shipClass} was destroyed!`, true);
                    gameState.recordShipLost();
                }
            }
        } else {
            // Record miss on our board
            gameState.playerBoard.shots.set(`${data.x},${data.y}`, { hit: false });
            ctx.playSound('splash');
            renderer.markMiss(gameState.playerBoard, data.x, data.y, false);
        }

        renderer.renderShipsStatus(gameState.ships, 'your-ships-status');

        // Show passive ability triggers (like splash_damage when your ship is destroyed)
        renderShotPassives(
            data.passiveResults, 'opponent-action',
            createAbilityRenderEnv({ gameState, renderer, abilityManager })
        );

        // Handle opponent's splash damage on our board
        if (data.splashResults && data.splashResults.applied) {
            renderer.showToast('Opponent Splash Damage: Adjacent cells hit!', true);
            for (const splash of data.splashResults.splashHits || []) {
                if (splash.alreadyHit) continue;
                gameState.playerBoard.shots.set(`${splash.x},${splash.y}`, { hit: splash.hit });
                if (splash.hit) {
                    renderer.markHit(gameState.playerBoard, splash.x, splash.y, false);
                    // Update ship hit tracking
                    const shipId = gameState.playerBoard.grid[splash.y]?.[splash.x];
                    if (shipId) {
                        const ship = gameState.ships.find(s => s.id === shipId);
                        if (ship) ship.hit(splash.x, splash.y);
                    }
                    if (splash.sunk) {
                        const ship = gameState.ships.find(s => s.class === splash.shipClass && !s.isSunk());
                        if (ship) {
                            renderer.showToast(`Your ${splash.shipClass} was destroyed!`, true);
                            gameState.recordShipLost();
                            renderer.markSunk(ship, false);
                        }
                    }
                } else {
                    renderer.markMiss(gameState.playerBoard, splash.x, splash.y, false);
                }
            }
            renderer.renderShipsStatus(gameState.ships, 'your-ships-status');
        }

        // Show opponent's extra shots remaining
        if (data.extraShotsRemaining) {
            renderer.showToast('Opponent has extra shots remaining!');
        }

        if (data.gameOver) {
            ctx.endGame(data.winner === gameState.playerId);
        }
    });

    networkManager.on('turn-change', (rawData?: unknown) => {
        const data = rawData as TurnChange;
        const wasMyTurn = gameState.isMyTurn;
        gameState.isMyTurn = data.currentTurn === gameState.playerId;
        // Increment turn counter when it becomes player's turn (after opponent's turn)
        if (gameState.isMyTurn && !wasMyTurn) {
            ctx.incrementTurnCount();
            // Clear fire processing lock when turn returns to us
            ctx.clearFireProcessing();
        }
        renderer.updateTurnIndicator(gameState.isMyTurn);
        abilityRenderer.renderAbilities(gameState.isMyTurn);
    });

    // Ability-specific network events
    networkManager.on('ability-result', (rawData?: unknown) => {
        const data = rawData as AbilityResult;
        if (data.abilityState) {
            abilityManager.updateFromServer(data.abilityState);
        }
        ctx.handleAbilityResult(data);
    });

    networkManager.on('ability-error', (rawData?: unknown) => {
        const data = rawData as ErrorMessage;
        renderer.showToast(data.message, true);
        abilityRenderer.renderAbilities(gameState.isMyTurn);
    });

    networkManager.on('opponent-ability', (rawData?: unknown) => {
        const data = rawData as OpponentAbility;
        // Update opponent's ability state if provided
        if (data.opponentAbilityState) {
            abilityManager.setOpponentState(data.opponentAbilityState);
        }

        abilityRenderer.showPassiveTriggered({
            abilityName: data.abilityName,
            message: `Opponent used ${data.abilityName}!`
        });

        // Effect-specific rendering (repair board-clear, board overlays/
        // animations) is routed through the registry in abilityRenderer.ts.
        const env = createAbilityRenderEnv({ gameState, renderer, abilityManager });
        renderOpponentAbility(data, env);

        const yourBoard = env.yourBoard;

        // Process incoming damage from opponent's damage abilities
        if (data.shotResults) {
            for (const shot of data.shotResults) {
                if (shot.isDecoy) {
                    // Our decoy was destroyed
                    abilityManager.revealDecoy(shot.x, shot.y);
                    if (yourBoard && shot.revealedCells) {
                        abilityRenderer.showDecoyReveal(shot.revealedCells, yourBoard);
                    }
                } else if (shot.hit) {
                    gameState.playerBoard.shots.set(`${shot.x},${shot.y}`, { hit: true });
                    renderer.markHit(gameState.playerBoard, shot.x, shot.y, false);
                    // Track ship hit
                    const shipId = gameState.playerBoard.grid[shot.y]?.[shot.x];
                    if (shipId) {
                        const hitShip = gameState.ships.find(s => s.id === shipId);
                        if (hitShip) hitShip.hit(shot.x, shot.y);
                    }
                    if (shot.sunk) {
                        renderer.showToast(`Your ${shot.shipClass} was destroyed!`, true);
                        gameState.recordShipLost();
                        const sunkShip = gameState.ships.find(s => s.class === shot.shipClass && s.isSunk());
                        if (sunkShip) {
                            renderer.markSunk(sunkShip, false, { x: shot.x, y: shot.y });
                        }
                    }
                } else {
                    gameState.playerBoard.shots.set(`${shot.x},${shot.y}`, { hit: false });
                    renderer.markMiss(gameState.playerBoard, shot.x, shot.y, false);
                }
            }
            renderer.renderShipsStatus(gameState.ships, 'your-ships-status');
        }

        // Process passive results from opponent's ability shots
        renderShotPassives(data.passiveResults, 'opponent-action', env);

        // Check game-over from opponent's ability damage
        if (data.gameOver) {
            ctx.endGame(data.winner === gameState.playerId);
        }
    });

    networkManager.on('passive-triggered', (rawData?: unknown) => {
        const data = rawData as PassiveTriggered;
        const isMyPassive = data.playerId === gameState.playerId;

        for (const result of data.results || []) {
            // Severian probe/mine turn-start mechanics carry a `type`
            // discriminator; everything else is a standard passive trigger.
            if (isMechanicResult(result)) {
                handleTurnStartMechanic(result, isMyPassive);
                continue;
            }

            // Standard passive triggers get notification
            abilityRenderer.showPassiveTriggered(result);

            // Handle splash damage activation
            if (result.buffType === 'splash_damage') {
                const targetBoard = isMyPassive
                    ? document.getElementById('your-board')
                    : document.getElementById('enemy-board');
                if (targetBoard) {
                    abilityRenderer.showSplashDamageActivation(targetBoard);
                }
            }

            // X1: Guerrilla Tactics glitch effect
            if (result.effect === 'guerrilla_tactics') {
                const board = isMyPassive
                    ? document.getElementById('enemy-board')
                    : document.getElementById('your-board');
                if (board) abilityRenderer.showGuerrillaTactics(board);
            }

            // X1: Iron Might surge effect
            if (result.effect === 'iron_might') {
                if (isMyPassive) abilityRenderer.showIronMight();
            }

            // Severian: Damage Control heal
            if (result.effect === 'damage_control') {
                if (isMyPassive && result.healedCells) {
                    // Apply healing to client board state
                    for (const cell of result.healedCells) {
                        gameState.playerBoard.shots.delete(`${cell.x},${cell.y}`);
                        const shipId = gameState.playerBoard.grid[cell.y]?.[cell.x];
                        if (shipId) {
                            const ship = gameState.ships.find(s => s.id === shipId);
                            if (ship) {
                                const cellIndex = ship.horizontal ? cell.x - ship.x : cell.y - ship.y;
                                ship.hits.delete(cellIndex);
                            }
                        }
                    }

                    const playerBoard = document.getElementById('your-board');
                    if (playerBoard) {
                        abilityRenderer.showDamageControlHeal(result.healedCells, playerBoard);
                    }
                    abilityRenderer.showPassiveTriggered(result);
                    // Re-render board after heal animation
                    setTimeout(() => {
                        renderer.renderYourBoard(gameState.playerBoard);
                        renderer.renderShipsStatus(gameState.ships, 'your-ships-status');
                    }, 1200);
                } else if (!isMyPassive && result.healedCells) {
                    // Opponent's Damage Control restored hull sections — clear our
                    // shot tracking so those cells can be targeted again, mirroring
                    // the active repair ability (opponent-ability handler above).
                    const enemyBoard = document.getElementById('enemy-board');
                    for (const cell of result.healedCells) {
                        gameState.enemyBoard.clearShot(cell.x, cell.y);
                        gameState.enemyShots.delete(`${cell.x},${cell.y}`);
                        if (enemyBoard) {
                            renderer.clearShotMarker(cell.x, cell.y, enemyBoard);
                            renderer.animateRepair(cell.x, cell.y, enemyBoard);
                        }
                    }
                }
            }
        }
    });

    networkManager.on('fire-error', (rawData?: unknown) => {
        const data = rawData as FireError;
        ctx.clearFireProcessing();
        renderer.showToast(data.message, true);
        // Reconcile turn state with the authoritative server turn. Unconditionally
        // assuming it's still our turn lets a stale "Not your turn" rejection flip
        // the client back to "my turn" while the server has moved on, leaving the
        // player clicking forever as every shot bounces. Fall back to the old
        // retry-friendly behavior only when the server sent no turn info.
        if (data.currentTurn !== undefined) {
            gameState.isMyTurn = data.currentTurn === gameState.playerId;
        } else {
            gameState.isMyTurn = true;
        }
        renderer.updateTurnIndicator(gameState.isMyTurn);
        abilityRenderer.renderAbilities(gameState.isMyTurn);
    });

    networkManager.on('opponent-disconnected', () => {
        renderer.showToast('Opponent disconnected', true);
        if (!gameState.gameOver) {
            ctx.endGame(true);
        }
    });

    networkManager.on('opponent-left', () => {
        renderer.showToast('Opponent left the game', true);
        // Tactical MP: tear the tactical module down too (its listeners and
        // screens would otherwise linger into the next game)
        if (gameState.isTactical) {
            void ctx.loadTacticalModule().then(m => m.cleanupTactical());
        }
        ctx.clearSession();
        gameState.reset();
        // Back to the menu: tournament/mode flags must not leak into the next game
        clearExternalModeFlags();
        abilityManager.reset();
        abilityRenderer.reset();
        renderer.clearBattleBoards();
        renderer.showScreen('menu-screen');
    });

    // The server no longer has our room (reaped after inactivity, or an action
    // hit a room that was already cleaned up). Retrying can never succeed, so
    // end the game and return to the menu — standard and tactical, MP and
    // server-hosted SP alike.
    networkManager.on('room-gone', (rawData?: unknown) => {
        const data = rawData as RoomGone;
        ctx.clearFireProcessing();
        // The game already ended (e.g. a tournament room is removed at game
        // over and a late action bounced): keep the player on their results.
        const onGameOverScreen = ['gameover-screen', 'tactical-gameover-screen']
            .some(id => document.getElementById(id)?.classList.contains('active'));
        if (gameState.gameOver || onGameOverScreen) return;
        ctx.stopRematchCountdown();
        ctx.clearSession();
        if (gameState.isTactical || window.tacticalActive) {
            ctx.loadTacticalModule()
                .then(mod => mod.cleanupTactical())
                .catch(err => console.warn('Tactical cleanup failed:', err));
        }
        // Keep a roguelike run resumable from the menu
        const runState = ctx.getRoguelikeRunState();
        if (gameState.isRoguelike && runState) {
            ctx.getRoguelike().saveActiveRun(runState);
        }
        gameState.reset();
        clearExternalModeFlags();
        abilityManager.reset();
        abilityRenderer.reset();
        renderer.clearBattleBoards();
        renderer.showToast(data?.message || 'This game is no longer available', true);
        renderer.showScreen('menu-screen');
        BugReport.setCurrentScreen('menu');
    });

    networkManager.on('rematch-requested', () => {
        renderer.showToast('Opponent wants a rematch!');
    });

    networkManager.on('rematch-error', (rawData?: unknown) => {
        const data = rawData as ErrorMessage | undefined;
        if (data?.retryable) {
            // Transient server throttle: the rematch window is still open
            const btn = document.getElementById('btn-rematch') as HTMLButtonElement | null;
            if (btn) btn.disabled = false;
            renderer.showToast(data.message, true);
            return;
        }
        ctx.stopRematchCountdown();
        const rematchBtn = document.getElementById('btn-rematch') as HTMLButtonElement;
        rematchBtn.textContent = 'REMATCH EXPIRED';
        rematchBtn.disabled = true;
        renderer.showToast('Rematch unavailable — opponent has left', true);
    });

    // Creating a multiplayer room failed (room cap, invalid faction, throttle):
    // the player stays on the faction screen and can try again
    networkManager.on('create-room-error', (rawData?: unknown) => {
        const data = rawData as ErrorMessage | undefined;
        renderer.showToast(data?.message || 'Could not create the room', true);
    });

    networkManager.on('rematch-start', () => {
        ctx.stopRematchCountdown();
        // Reset game state but keep room and player info
        const roomCode = gameState.roomCode;
        const playerId = gameState.playerId;
        const playerName = gameState.playerName;
        const opponentName = gameState.opponentName;
        const opponentFaction = gameState.opponentFaction;
        const faction = gameState.faction!;
        const wasQuickplay = gameState.isQuickplay;
        const wasTactical = gameState.isTactical;

        gameState.reset();
        gameState.mode = 'multiplayer';
        gameState.roomCode = roomCode;
        gameState.playerId = playerId;
        gameState.playerName = playerName;
        gameState.opponentName = opponentName;
        gameState.opponentFaction = opponentFaction;
        gameState.isQuickplay = wasQuickplay;
        gameState.isTactical = wasTactical;
        gameState.initializeShips(faction);

        // Re-initialize abilities
        const factions = getFactions();
        const factionData = factions[faction];
        if (factionData && factionData.abilityData) {
            abilityManager.initialize(factionData.abilityData);
        }
        abilityRenderer.reset();

        // Update remote driver room code
        const driver = ctx.getDriver();
        if (driver) driver.updateRoomCode(roomCode!);

        // Clear battle boards before going to placement
        renderer.clearBattleBoards();

        renderer.showScreen('placement-screen');
        ctx.initializePlacement();

        // Reset rematch button
        const rematchBtn = document.getElementById('btn-rematch') as HTMLButtonElement;
        rematchBtn.textContent = 'REMATCH';
        rematchBtn.disabled = false;
        rematchBtn.blur(); // Clear focus/active state
    });

    networkManager.on('disconnected', () => {
        if (gameState.gameStarted && !gameState.gameOver) {
            renderer.showToast('Disconnected from server', true);
        }
    });

    // Quickplay handlers
    networkManager.on('quickplay-joined', (rawData?: unknown) => {
        const data = rawData as QuickplayQueueUpdate;
        updateQueueDisplay(data.position, data.estimatedWait);
    });

    networkManager.on('quickplay-update', (rawData?: unknown) => {
        const data = rawData as QuickplayQueueUpdate;
        updateQueueDisplay(data.position, data.estimatedWait);
    });

    networkManager.on('quickplay-matched', (rawData?: unknown) => {
        const data = rawData as QuickplayMatched;
        const queueScreen = document.getElementById('queue-screen')!;
        queueScreen.classList.add('match-found');

        // Update title to show match found
        const title = queueScreen.querySelector('.screen-title');
        if (title) title.textContent = 'OPPONENT FOUND!';

        // Store player and opponent info
        gameState.playerId = data.playerId;
        gameState.opponentName = data.opponentName;
        gameState.opponentFaction = data.opponentFaction;
        gameState.roomCode = data.roomCode;
        gameState.isMultiplayer = true;
        gameState.isQuickplay = true;

        // Save session for reconnection
        if (data.sessionToken) {
            ctx.saveSession(data.roomCode, data.sessionToken, gameState.faction);
        }

        // Create remote driver for MP battle actions
        const driver = ctx.getDriver();
        if (driver) driver.dispose();
        const newDriver = new StandardRemoteDriver(networkManager, data.roomCode);
        ctx.setDriver(newDriver);

        // Set enemy faction colors for ship graphics
        const factions = getFactions();
        const enemyFactionData = factions[data.opponentFaction];
        if (enemyFactionData?.colors) {
            renderer.setFactionColors(null, enemyFactionData.colors);
        }

        // Transition to placement (or tactical) after delay
        setTimeout(async () => {
            queueScreen.classList.remove('match-found');
            if (title) title.textContent = 'SEARCHING FOR OPPONENT';
            ctx.setQuickplayMode(false);

            // Tactical MP: launch tactical mode instead of standard placement
            if (data.gameType === 'tactical') {
                const tacticalMod = await ctx.loadTacticalModule();
                await tacticalMod.initTactical({
                    faction: gameState.faction,
                    mode: 'multiplayer',
                    socket: networkManager,
                    mainRenderer: renderer,
                    roomCode: data.roomCode
                });
                return;
            }

            ctx.initializePlacement();
            renderer.showScreen('placement-screen');
        }, 1500);
    });

    networkManager.on('quickplay-error', (rawData?: unknown) => {
        const data = rawData as ErrorMessage;
        renderer.showToast(data.message, true);
        ctx.setQuickplayMode(false);
        renderer.showScreen('lobby-screen');
    });

    // Tournament result events
    networkManager.on('tournament-match-complete', (rawData?: unknown) => {
        const data = rawData as TournamentMatchComplete;
        // Store tournament result info for game-over screen
        gameState.tournamentMatchResult = {
            matchId: data.matchId,
            tournamentId: data.tournamentId,
            winnerId: data.winnerId,
            winnerName: data.winnerName
        };
        // Auto-confirm the result
        if (gameState.tournamentCode) {
            networkManager.confirmTournamentResult(data.matchId, gameState.tournamentCode);
        }
    });

    networkManager.on('tournament-result-confirmed', (rawData?: unknown) => {
        const data = rawData as TournamentResultConfirmed;
        if (data.success) {
            renderer.showToast('Tournament result recorded');
        }
    });
}

// The mine hit that finished `sunk` — the sink cascade's origin cell.
function mineLastHit(sunk: MineSunkShip, hitTargets: Coord[]): Coord | null {
    for (let i = hitTargets.length - 1; i >= 0; i--) {
        const t = hitTargets[i];
        if (sunk.cells.some(c => c.x === t.x && c.y === t.y)) return t;
    }
    return null;
}

// Severian Principate probe/mine turn-start mechanics (split out of the
// passive-triggered handler; behavior unchanged — these branches and the
// standard-passive branches above were mutually exclusive already).
function handleTurnStartMechanic(result: TurnStartMechanicResult, isMyPassive: boolean): void {
    const { renderer, abilityRenderer } = ctx;

    // Severian: Probe pulse results
    if (result.type === 'probe_pulse' && isMyPassive) {
        const enemyBoard = document.getElementById('enemy-board');
        if (enemyBoard && result.probePos) {
            abilityRenderer.showProbePulse(
                result.probePos,
                result.detectionRadius || 3,
                result.hasContact,
                enemyBoard
            );
        }
        abilityRenderer.showPassiveTriggered({
            abilityId: 'probe_network',
            abilityName: 'Probe Network',
            message: result.message
        });
    }

    // Severian: Probe expired
    if (result.type === 'probe_expired' && isMyPassive) {
        const enemyBoard = document.getElementById('enemy-board');
        if (enemyBoard && result.probePos) {
            abilityRenderer.removeProbeMarker(result.probePos, enemyBoard);
        }
        renderer.showToast(result.message);
    }

    // Severian: Mine detonation
    if (result.type === 'mine_detonation' && isMyPassive) {
        const enemyBoard = document.getElementById('enemy-board');
        if (enemyBoard) {
            abilityRenderer.showMineDetonation(
                result.minePos,
                result.hitTargets,
                enemyBoard
            );
        }
        // Process mine hits on enemy board
        if (result.hitTargets) {
            for (const target of result.hitTargets) {
                gameState.enemyShots.set(`${target.x},${target.y}`, { hit: true });
                renderer.markHit(null, target.x, target.y, true);
            }
            // Ships the mine sank: same treatment as a sinking shot-result
            // (cells recorded, sink cascade, enemy fleet status), #66
            const sunkShips = result.sunkShips ?? [];
            for (const sunk of sunkShips) {
                for (const cell of sunk.cells) {
                    gameState.enemyShots.set(`${cell.x},${cell.y}`, { hit: true });
                }
                renderer.markSunk({ getCells: () => sunk.cells }, true, mineLastHit(sunk, result.hitTargets));
                if (gameState.opponentShipStatus) {
                    const entry = gameState.opponentShipStatus.find(s => s.class === sunk.shipClass && !s.sunk);
                    if (entry) {
                        entry.sunk = true;
                        renderer.renderShipsStatus(
                            gameState.opponentShipStatus.map(s => ({ class: s.class, isSunk: () => s.sunk })),
                            'enemy-ships-status'
                        );
                    }
                }
            }
            if (sunkShips.length > 0) {
                ctx.playSound('destroyed');
                renderer.showToast(`Mine destroyed ${sunkShips.map(sh => sh.shipClass || sh.name).join(' and ')}!`);
            }
            renderer.renderEnemyBoard(gameState.enemyShots);
        }
        abilityRenderer.showPassiveTriggered({
            abilityId: 'hunter_killer_mine',
            abilityName: 'Hunter-Killer Mine',
            message: result.message
        });
    }

    // Severian: Mine seeking (no target found)
    if (result.type === 'mine_seeking' && isMyPassive) {
        renderer.showToast(result.message);
    }

    // Severian: Mine expired
    if (result.type === 'mine_expired' && isMyPassive) {
        const enemyBoard = document.getElementById('enemy-board');
        if (enemyBoard && result.minePos) {
            abilityRenderer.removeMineMarker(result.minePos, enemyBoard);
        }
        renderer.showToast(result.message);
    }

    // Handle opponent's Severian mechanics (probe/mine results on your board)
    if (result.type === 'mine_detonation' && !isMyPassive) {
        // Opponent's mine hit our ships
        const yourBoard = document.getElementById('your-board');
        if (yourBoard && result.hitTargets) {
            abilityRenderer.showMineDetonation(
                result.minePos,
                result.hitTargets,
                yourBoard
            );
            // Record damage to our board
            for (const target of result.hitTargets) {
                gameState.playerBoard.shots.set(`${target.x},${target.y}`, { hit: true });
                renderer.markHit(gameState.playerBoard, target.x, target.y, false);
                const shipId = gameState.playerBoard.grid[target.y]?.[target.x];
                if (shipId) {
                    const ship = gameState.ships.find(s => s.id === shipId);
                    if (ship) ship.hit(target.x, target.y);
                }
            }
            // Our ships the mine sank: sink cascade + fleet status, like an
            // opponent-shot kill (#66)
            const sunkShips = result.sunkShips ?? [];
            for (const sunk of sunkShips) {
                gameState.recordShipLost();
                const ship = gameState.ships.find(s => s.id === sunk.id);
                renderer.markSunk(ship ?? { getCells: () => sunk.cells }, false, mineLastHit(sunk, result.hitTargets));
            }
            if (sunkShips.length > 0) {
                ctx.playSound('destroyed');
                const names = sunkShips.map(sh => sh.shipClass || sh.name).join(' and ');
                renderer.showToast(`Your ${names} ${sunkShips.length > 1 ? 'were' : 'was'} destroyed by mine!`, true);
            }
            renderer.renderShipsStatus(gameState.ships, 'your-ships-status');
        }
    }
}
